import { useSearchParams } from '@remix-run/react'
import { useEffect, useMemo } from 'react'
import { Toolbox } from '~/plugins/toolbox/shared'
import { ConfigurationType, StepType } from '../../common'
import { useIfConditions } from './useIfConditions'

type Props = {
  values: any
  configuration: ConfigurationType
  onSubmitForm: (values: any, step: StepType) => void
  onSubmitStep: (values: any, step: StepType) => void
}

export const useNavigationParams = ({
  values = {},
  configuration,
  onSubmitForm,
  onSubmitStep,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const stepKey = searchParams.get('step-key')

  const ifConditions = useIfConditions()

  const steps: StepType[] = useMemo(() => {
    const items = (configuration?.steps ?? [])
      .filter(step => !!step.key)
      .filter(step => ifConditions.run(values, step.ifConditions))

    return items
  }, [values])

  const step = useMemo(() => {
    const stepDefault = steps[0]
    const stepBackup = steps.find(step => step.key === values['_stepKey'])
    const stepFound = steps.find(step => step.key === stepKey)
    return stepFound ?? stepBackup ?? stepDefault
  }, [stepKey, steps])

  const cleanStepsFromValues = (valuesToClean: any) => {
    const stepsHidden = (configuration?.steps ?? [])
      .filter(step => !!step.key)
      .filter(candidate => !steps.find(step => step.key === candidate.key))

    const valuesCleaned = { ...valuesToClean }

    for (const stepHidden of stepsHidden) {
      delete valuesCleaned[stepHidden.key]
    }

    return valuesCleaned
  }

  useEffect(() => {
    if (step && step.key !== stepKey) {
      searchParams.set('step-key', step.key)
      setSearchParams(searchParams)
    }
  }, [step, stepKey])

  const index = useMemo(() => {
    return steps.findIndex(candidate => candidate.key === step?.key)
  }, [step, steps])

  const nextStep = (valuesUpdated: any) => {
    const stepNext = steps[index + 1]
    const stepDefault = steps[0]
    const valuesTrimmed = Toolbox.Utility.trimNullValues(valuesUpdated)

    if (index === steps.length - 1) {
      const valuesClean = cleanStepsFromValues(valuesTrimmed)

      onSubmitForm(
        {
          ...valuesClean,
          _stepKey: stepDefault?.key ?? step.key,
          _formStatus: 'submitted',
        },
        step,
      )
      return
    }

    onSubmitStep(
      {
        ...valuesTrimmed,
        _stepKey: stepNext?.key ?? step.key,
      },
      step,
    )

    if (stepNext) {
      searchParams.set('step-key', stepNext.key)
      setSearchParams(searchParams)
    }
  }

  const previousStep = useMemo(() => {
    const stepBefore = steps[index - 1]
    if (stepBefore) {
      return () => {
        searchParams.set('step-key', stepBefore.key)
        setSearchParams(searchParams)
      }
    }
  }, [steps, index])

  const isLastStep = useMemo(() => {
    if (index < 0 || !steps) {
      return false
    }

    return index === steps.length - 1
  }, [index, steps])

  return {
    isLastStep,
    index,
    step,
    steps,
    nextStep,
    previousStep,
  }
}
